/**
 * CLI-001 — Queries Clients (list, detail, nearby, deletion preview).
 *
 * Extrait de useClients.ts pour respecter la limite de 300 lignes.
 */

import { keepPreviousData, queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from '@/lib/api'
import { handleApiError } from '@/lib/api-error'
import { queryKeys } from '@/lib/queryKeys'

import {
    CLIENTS_ENDPOINT,
    buildClientQuery,
    clientHeaders,
    enrichmentPollDelay,
    isEnrichmentInProgress,
} from './useClients.helpers'
import type {
    Client,
    ClientDuplicatesResponse,
    ClientListFilters,
    ClientListResponse,
    DeletionPreview,
    NearbyClient,
} from './useClients.types'

export function useClientList(entityId: string | null | undefined, filters?: ClientListFilters) {
    return useQuery<ClientListResponse>({
        queryKey: queryKeys.clients.list(entityId ?? '', filters as Record<string, unknown>),
        queryFn: async () => {
            const response = await apiFetch(`${CLIENTS_ENDPOINT}${buildClientQuery(filters)}`, {
                headers: clientHeaders(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId,
        staleTime: 30_000,
        // Garde la liste précédente affichée pendant que la nouvelle requête
        // (typiquement après un changement de `q` debouncé) est en cours.
        // Évite le flicker "items → empty → items" qui faisait sauter la
        // hauteur du panneau dans ClientPicker mode inline (cf. revue UX
        // 2026-05-06 — typing "MANG" produisait des sauts visibles).
        placeholderData: keepPreviousData,
    })
}

export function useClientDetail(
    clientId: string | null | undefined,
    entityId: string | null | undefined,
) {
    const queryClient = useQueryClient()
    return useQuery<Client>({
        // `entityId` est dans la clé : un switch d'entité change la clé →
        // React Query refetch automatiquement avec le nouvel header
        // X-Entity-Id, et le bandeau ↑ Hérité bascule sans rafraîchir la page.
        queryKey: queryKeys.clients.detail(clientId ?? '', entityId ?? ''),
        queryFn: async () => {
            const response = await apiFetch(`${CLIENTS_ENDPOINT}/${clientId}`, {
                headers: clientHeaders(entityId),
            })
            if (!response.ok) await handleApiError(response)
            const fresh = (await response.json()) as Client
            // Bug fix 2026-05-04 itération 9 : invalider les queries
            // d'enrichissement (Santé entreprise) dès qu'un cycle vient de
            // s'achever pour rafraîchir l'onglet Santé sans intervention.
            // Itération 15 : ajout de `scoring-history` dans la liste — sans
            // ça, la sparkline 12 mois restait sur son cache vide même après
            // que le backend ait écrit les snapshots (race entre le backfill
            // synchrone côté serveur et la query React Query côté client).
            if (
                fresh.enrichment.enrichment_fetched_at &&
                fresh.legal?.siren
            ) {
                const siren = fresh.legal.siren
                queryClient.invalidateQueries({ queryKey: ['enrichment', 'identity', siren] })
                queryClient.invalidateQueries({ queryKey: ['enrichment', 'financials', siren] })
                queryClient.invalidateQueries({ queryKey: ['enrichment', 'scoring', siren] })
                queryClient.invalidateQueries({ queryKey: ['enrichment', 'scoring-history', siren] })
                queryClient.invalidateQueries({ queryKey: ['enrichment', 'events', siren] })
            }
            return fresh
        },
        enabled: !!clientId && !!entityId,
        staleTime: 30_000,
        // Poll auto tant que `requested_at > fetched_at` — couvre la fenêtre
        // où INPI / BODACC s'enrichissent en arrière-plan après création.
        // Backoff exponentiel — cf. `enrichmentPollDelay` ci-dessus.
        refetchInterval: (query) => {
            const data = query.state.data
            if (!data) return false
            return isEnrichmentInProgress(data) ? enrichmentPollDelay(data) : false
        },
    })
}

/**
 * Pré-vérification doublons clients (CLI-001 §7.0bis) — les options de la requête.
 *
 * ⭐ UNE fabrique pour DEUX appelants : le hook `useCheckClientDuplicates` (le bandeau qui suit
 * la saisie) et l'appel IMPÉRATIF `queryClient.fetchQuery(...)` du champ « chercher ou créer »,
 * qui rejoue le contrôle sur l'entreprise choisie dans l'annuaire. Même clé, même `queryFn` :
 * deux constructions de la requête finiraient par diverger, et le cache ne servirait plus.
 *
 * Deux modes :
 * - **Société** : `name` >= 2 caractères → mots entiers normalisés du nom, plus SIREN/SIRET
 *   identique. Le contrôle part dès qu'il a l'un des trois : un nom valide, un SIREN de 9
 *   caractères ou un SIRET de 14. Un nom plus court n'est pas envoyé : la requête part alors sur
 *   le SIREN ou le SIRET seuls (entreprise de l'annuaire sans dénomination). Un nom de plus de
 *   {@link NOM_MAX_DU_CONTROLE} caractères est coupé à cette longueur : la route le refuserait.
 *   ⚠️ Un appelant qui n'est pas en mode société ne passe donc ni SIREN ni SIRET.
 * - **Particulier** : `firstName` (>= 1) + `lastName` (>= 2) → nom ET prénom proches.
 *
 * `typing` dit au serveur que le nom est EN COURS DE FRAPPE : le DERNIER mot tapé peut alors
 * s'apparier comme DÉBUT d'un mot de la fiche (« Decat » → Decathlon). Il ne part qu'en mode
 * société, et seulement avec un nom — le serveur l'ignore sans lui, et la clé n'en garde que la
 * valeur utile, pour que les deux réglages partagent leur cache dans ces cas-là.
 *
 * `champEnCours` est son pendant en mode PARTICULIER : il nomme le champ EN COURS DE FRAPPE
 * (`first_name` ou `last_name`), et part en `typing_field`. Le dernier mot de ce champ seul, à
 * partir de 4 lettres, peut alors s'apparier comme DÉBUT d'un mot de la fiche (« Dupo » →
 * DUPONT). `null` (le défaut) sur des valeurs FIGÉES : découpées au passage en Particulier,
 * préremplies, rouvertes — un début n'y vaut rien. Il ne part qu'en mode particulier, et la clé
 * n'en garde que la valeur utile, en DERNIÈRE position.
 *
 * ⚠️ Il n'y a AUCUN debounce ici : `staleTime` ne retarde rien, il évite seulement de
 * redemander une clé déjà servie. Un écran dont le nom suit la frappe diffère lui-même la
 * valeur qu'il passe (`useDebounce`).
 *
 * Avertissement doux et non bloquant (le garde-fou dur reste le 409 SIRET_DUPLICATE levé à la
 * création).
 */
/** La longueur maximale du `name` que la route accepte (`max_length=255`, au-delà : 422). */
export const NOM_MAX_DU_CONTROLE = 255

/** Le champ d'un particulier EN COURS DE FRAPPE, tel que la route l'attend (`typing_field`). */
export type ChampEnCoursDeFrappe = 'first_name' | 'last_name'

/**
 * Vrai si la clé de {@link optionsDuControleDeDoublons} est celle d'un contrôle de PERSONNE
 * (prénom et nom, la règle de `particulierMode`), faux pour celui d'une société.
 */
export function estUnControleDePersonne(cle: readonly unknown[]): boolean {
    return String(cle[7] ?? '').length >= 1 && String(cle[8] ?? '').length >= 2
}

export function optionsDuControleDeDoublons(
    entityId: string | null | undefined,
    name: string,
    siren: string | null = null,
    siret: string | null = null,
    firstName: string | null = null,
    lastName: string | null = null,
    typing = false,
    champEnCours: ChampEnCoursDeFrappe | null = null,
) {
    // Coupé par POINTS DE CODE, comme la route compte : `slice` couperait une paire de
    // substitution en deux, et un émoji en 255ᵉ position partirait en caractère de remplacement.
    const nameTrim = Array.from(name.trim()).slice(0, NOM_MAX_DU_CONTROLE).join('').trimEnd()
    const sirenTrim = siren?.trim() ?? ''
    const siretTrim = siret?.trim() ?? ''
    const firstTrim = firstName?.trim() ?? ''
    const lastTrim = lastName?.trim() ?? ''

    const particulierMode = firstTrim.length >= 1 && lastTrim.length >= 2
    const nomValide = Array.from(nameTrim).length >= 2 // le minimum de la route, en points de code
    const avecIdentifiant = sirenTrim.length === 9 || siretTrim.length === 14
    const enFrappe = typing && !particulierMode && nomValide
    const champEnvoye = particulierMode ? champEnCours : null
    const enabled = !!entityId && (particulierMode || nomValide || avecIdentifiant)

    return queryOptions<ClientDuplicatesResponse>({
        // ⚠️ Clé LITTÉRALE (voir `useApercuDesChampsObligatoires`) ; `enFrappe` puis `champEnvoye`
        // en FIN de clé (`estUnControleDePersonne` lit les positions 7 et 8).
        queryKey: [
            'crm',
            'clients',
            'check-duplicates',
            entityId ?? '',
            nameTrim.toLowerCase(),
            sirenTrim,
            siretTrim,
            firstTrim.toLowerCase(),
            lastTrim.toLowerCase(),
            enFrappe,
            champEnvoye,
        ],
        queryFn: async () => {
            const sp = new URLSearchParams()
            if (particulierMode) {
                sp.set('first_name', firstTrim)
                sp.set('last_name', lastTrim)
                if (champEnvoye) sp.set('typing_field', champEnvoye)
            } else {
                // ⛔ La route refuse un `name` de moins de 2 caractères (422), mais cherche par
                // SIREN ou SIRET seuls : une entreprise « Sans nom » de l'annuaire se contrôle
                // donc sur ses identifiants, dans le bandeau comme au choix.
                if (nomValide) sp.set('name', nameTrim)
                if (sirenTrim.length === 9) sp.set('siren', sirenTrim)
                if (siretTrim.length === 14) sp.set('siret', siretTrim)
                if (enFrappe) sp.set('typing', 'true')
            }
            const response = await apiFetch(`${CLIENTS_ENDPOINT}/check-duplicates?${sp}`, {
                headers: clientHeaders(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled,
        // Cache court — l'utilisateur peut taper rapidement et changer le nom
        // plusieurs fois au fil de la frappe.
        staleTime: 10_000,
    })
}

/**
 * Le contrôle de doublons qui suit la saisie. ⛔ L'ordre des paramètres est FIGÉ : l'assistant
 * de transformation d'un appel d'offres les lit par position (3 tests). `typing`, puis
 * `champEnCours`, s'ajoutent en FIN.
 */
export function useCheckClientDuplicates(
    entityId: string | null | undefined,
    name: string,
    siren: string | null = null,
    siret: string | null = null,
    firstName: string | null = null,
    lastName: string | null = null,
    typing = false,
    champEnCours: ChampEnCoursDeFrappe | null = null,
) {
    return useQuery(
        optionsDuControleDeDoublons(
            entityId, name, siren, siret, firstName, lastName, typing, champEnCours,
        ),
    )
}

export function useNearbyClients(
    entityId: string | null | undefined,
    lat: number | null,
    lng: number | null,
    radiusKm: number,
    includeDescendants = true,
) {
    return useQuery<{ items: NearbyClient[] }>({
        queryKey: queryKeys.clients.nearby(entityId ?? '', lat ?? 0, lng ?? 0, radiusKm),
        queryFn: async () => {
            const sp = new URLSearchParams({
                lat: String(lat),
                lng: String(lng),
                radius_km: String(radiusKm),
                include_descendants: String(includeDescendants),
            })
            const response = await apiFetch(`${CLIENTS_ENDPOINT}/nearby?${sp}`, {
                headers: clientHeaders(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!entityId && lat !== null && lng !== null,
        staleTime: 60_000,
    })
}

export function useClientDeletionPreview(
    clientId: string | null | undefined,
    entityId: string | null | undefined,
) {
    return useQuery<DeletionPreview>({
        queryKey: ['clients', 'deletion-preview', clientId ?? ''],
        queryFn: async () => {
            const response = await apiFetch(`${CLIENTS_ENDPOINT}/${clientId}/deletion-preview`, {
                headers: clientHeaders(entityId),
            })
            if (!response.ok) await handleApiError(response)
            return response.json()
        },
        enabled: !!clientId && !!entityId,
        // No caching: deps change as soon as an invoice/session is added
        staleTime: 0,
        gcTime: 0,
    })
}
